<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, onMounted, ref, getCurrentInstance } from 'vue'
import Tooltip from '@/components/Atoms/Tooltip.vue';
import { useStore } from 'vuex'
import _ from 'lodash';
import { track, EVENTS } from '@/lib/tracking'
import StreamService from '@/services/StreamService';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import { useNotification } from "@kyvg/vue3-notification"
import FormButton from '../../components/Atoms/FormButton.vue';
import FormInput from '../../components/Atoms/FormInput.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconCross from '../../components/icon/IconCross.vue';
import Alert from '../../components/Atoms/Alert.vue';

const { notify } = useNotification()

const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id');
		}
	}
})

const streamId = ref(null)
const webhooks = ref([])
const newWebhook = ref({ name: null, url: null, enabled: true })
const showAddForm = ref(false)
const deleteTarget = ref(null)
const deleteTitle = ref(null)

const streamSub = computed(() => {
	const subscriptions = store.state.User.subscriptions
	return subscriptions.find(
		(sub) =>
			(sub.category && sub.category.includes(props.stream.type)) ||
			_.get(sub, ['package', 'category'], '').includes(props.stream.type)
	)
})

const webhooksEnabled = computed(() => {
	return _.get(
		streamSub.value,
		'definitionOverride.webhooks',
		_.get(streamSub.value, ['package', 'definition', 'webhooks'], false)
	)
})

const maxWebhooks = computed(() => {
	return _.get(
		streamSub.value,
		'definitionOverride.maxWebhooks',
		_.get(streamSub.value, ['package', 'definition', 'maxWebhooks'], 0)
	)
})

const isAccess = computed(() => {
	if (!webhooksEnabled.value) return false
	if (maxWebhooks.value > 0) return true
	const allowedUserIds = [
		'67776e31ae1d2aa78aa43811', // support@goodsurfnow.com
		'64d2da616b40a8311c4f46ca', // bennobarr@gmail.com
		'666b811bda8c423d1b5800e1', // admin@goodsurfnow.com
		'682844396f7cd6438223a9b9', // shipluj@groupsrk.com
	]
	return allowedUserIds.some((id) => id === store.state.User._id)
})

const canAddMore = computed(() => {
	if (maxWebhooks.value <= 0) return true
	return webhooks.value.length < maxWebhooks.value
})

const tooltipMessage = computed(() => {
	return `Maximum of ${maxWebhooks.value} webhook${maxWebhooks.value === 1 ? '' : 's'} allowed per stream. <span class="underline" onclick="Intercom('show')">Contact sales</span> to increase the limit.`
})

onMounted(async () => {
	streamId.value = props.stream._id
	const endpoints = props.stream.metadata.webhookEndpoints
	if (Array.isArray(endpoints) && endpoints.length > 0) {
		webhooks.value = _.cloneDeep(endpoints)
	}
})

const openAddForm = () => {
	newWebhook.value = { name: null, url: null, enabled: true }
	showAddForm.value = true
}

const cancelAdd = () => {
	showAddForm.value = false
}

const saveNewWebhook = async () => {
	try {
		const params = {
			name: newWebhook.value.name,
			url: newWebhook.value.url?.trim(),
			enabled: newWebhook.value.enabled
		}
		const res = await StreamService.createStreamWebhookEndpoint(streamId.value, params)
		if (Array.isArray(res)) {
			webhooks.value = _.cloneDeep(res)
		} else {
			webhooks.value.push(res)
		}
		showAddForm.value = false
		notify({ group: 'success', text: 'Webhook endpoint added' })
		track(EVENTS.WEBHOOK_ADDED, {
			streamId: props.stream._id,
			streamType: props.stream.type,
			webhookEnabled: params.enabled,
		})
	} catch (e) {
		notify({ group: 'error', text: e?.message || 'Failed to save webhook' })
	}
}

const toggleWebhook = async (index) => {
	const wh = webhooks.value[index]
	try {
		const params = { name: wh.name, url: wh.url, enabled: !wh.enabled }
		const res = await StreamService.updateStreamWebhookEndpoint(streamId.value, wh._id, params)
		if (Array.isArray(res)) {
			webhooks.value = _.cloneDeep(res)
		} else {
			webhooks.value[index] = res
		}
	} catch (e) {
		notify({ group: 'error', text: e?.message || 'Failed to toggle webhook' })
	}
}

const requestDelete = (index) => {
	deleteTarget.value = index
	deleteTitle.value = `Delete '${webhooks.value[index].name}' webhook?`
	showModal('modal-confirm-webhook-delete')
}

const confirmDelete = async () => {
	const index = deleteTarget.value
	const wh = webhooks.value[index]
	try {
		await StreamService.deleteStreamWebhookEndpoint(streamId.value, wh._id)
		webhooks.value.splice(index, 1)
		notify({ group: 'success', text: 'Webhook removed' })
	} catch (e) {
		notify({ group: 'error', text: e?.message || 'Failed to delete webhook' })
	}
	deleteTarget.value = null
}
</script>

<template>
	<div>
		<div class="flex flex-col text-center" v-if="!isAccess">
			<p class="text-lg !mt-6 !mb-2">You don't have access to this feature</p>
			<p class="text-sm text-surface-8">
				<router-link to="/subscribe?category=live" class="hover:no-underline">Upgrade</router-link> or
				<FormButton type="link" customClasses="text-surface-8 underline" onclick="Intercom('show')">contact us</FormButton>
				to access this feature
			</p>
		</div>
		<div v-else>
			<Alert class="!mb-4" text="These URLs are used for events such as start and stop stream events." />

			<div v-for="(wh, index) in webhooks" :key="wh._id || index"
				class="flex items-center gap-x-3 py-3 border-b border-surface-3">
				<div class="flex-1 min-w-0">
					<p class="text-sm font-medium text-surface-9 truncate !mb-0">{{ wh.name }}</p>
					<p class="text-xs text-surface-7 truncate !mb-0">{{ wh.url }}</p>
				</div>
				<span class="text-xs px-2 py-0.5 rounded shrink-0"
					:class="wh.enabled ? 'bg-success/10 text-success' : 'bg-surface-3 text-surface-7'">
					{{ wh.enabled ? 'Active' : 'Disabled' }}
				</span>
				<FormButton class="shrink-0" type="secondary" @click="toggleWebhook(index)"
					:label="wh.enabled ? 'Disable' : 'Enable'" />
				<FormButton class="shrink-0" type="link" @click="requestDelete(index)" isIcon>
					<icon-base><icon-cross /></icon-base>
				</FormButton>
			</div>

			<div v-if="showAddForm" class="flex items-end gap-x-3 !mt-4">
				<FormInput label="Webhook Name" id="new-name" v-model="newWebhook.name" placeholder="My webhook" />
				<FormInput label="Webhook URL" id="new-url" v-model="newWebhook.url" placeholder="http://..." />
				<FormButton class="shrink-0" @click="saveNewWebhook"
					:disabled="!newWebhook.name || !newWebhook.url" label="Save" />
				<FormButton class="shrink-0" type="secondary" @click="cancelAdd" label="Cancel" />
			</div>

			<div class="inline-block !mt-4">
				<Tooltip v-if="!canAddMore" :content="tooltipMessage" html>
					<FormButton type="secondary" disabled>Add webhook</FormButton>
				</Tooltip>
				<FormButton v-else-if="!showAddForm" type="secondary" @click="openAddForm">Add webhook</FormButton>
			</div>

			<confirm-modal modal-id="modal-confirm-webhook-delete" :message="deleteTitle"
				body="The link will be removed and events won't be sent anymore." ok-text="Remove" cancel-text="Cancel"
				@modal-confirm="confirmDelete" />
		</div>
	</div>
</template>
